import { useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Sidebar    from "./components/Sidebar";
import { ToastHost } from "./shared/toast";

// Route-based code-splitting: each page is its own chunk, loaded on demand.
// Login now downloads only a tiny bundle instead of the whole panel → fast load.
const Login        = lazy(() => import("./pages/Login"));
const Register     = lazy(() => import("./pages/Register"));
const Dashboard    = lazy(() => import("./pages/Dashboard"));
const Products     = lazy(() => import("./pages/Products"));
const SellExisting = lazy(() => import("./pages/SellExisting"));
const Variants     = lazy(() => import("./pages/Variants"));
const Orders       = lazy(() => import("./pages/Orders"));
const Earnings     = lazy(() => import("./pages/Earnings"));
const Profile      = lazy(() => import("./pages/Profile"));
const Notifications = lazy(() => import("./pages/Notifications"));

function PageLoader() {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 14 }}>
      Loading…
    </div>
  );
}

function PrivateLayout({ children }) {
  const { seller, loading } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f8fa" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, fontWeight: 900, color: "#6366f1", marginBottom: 12 }}>VARAii</div>
        <div style={{ fontSize: 14, color: "#94a3b8" }}>Loading…</div>
      </div>
    </div>
  );
  if (!seller) return <Navigate to="/login" replace />;
  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      {/* dark overlay behind the drawer (mobile only) */}
      <div className={`seller-nav-overlay${navOpen ? " open" : ""}`} onClick={() => setNavOpen(false)} />
      <main className="seller-main" style={{ flex: 1, background: "#f8fafc", minHeight: "100vh" }}>
        {/* mobile top bar with hamburger (hidden on desktop) */}
        <div className="seller-mobilebar">
          <button aria-label="Open menu" onClick={() => setNavOpen(true)} style={{
            background: "transparent", border: "none", color: "#fff", fontSize: 22,
            cursor: "pointer", lineHeight: 1, padding: 4
          }}>☰</button>
          <span style={{ fontWeight: 900, color: "#fff", fontSize: 17, letterSpacing: -0.3 }}>
            VARA<span style={{ color: "#fbbf24" }}>ii</span>
            <span style={{ color: "#94a3b8", fontWeight: 700, fontSize: 11, marginLeft: 6, letterSpacing: "0.06em" }}>SELLER</span>
          </span>
        </div>
        <div className="seller-content">{children}</div>
      </main>
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login"    element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/" element={<PrivateLayout><Dashboard /></PrivateLayout>} />
      <Route path="/products" element={<PrivateLayout><Products /></PrivateLayout>} />
      <Route path="/sell-existing" element={<PrivateLayout><SellExisting /></PrivateLayout>} />
      <Route path="/products/:id/variants" element={<PrivateLayout><Variants /></PrivateLayout>} />
      <Route path="/orders"   element={<PrivateLayout><Orders /></PrivateLayout>} />
      <Route path="/earnings" element={<PrivateLayout><Earnings /></PrivateLayout>} />
      <Route path="/profile"  element={<PrivateLayout><Profile /></PrivateLayout>} />
      <Route path="/notifications" element={<PrivateLayout><Notifications /></PrivateLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastHost />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
